/**
 * Pure helpers for the propose-a-role form — kept out of the component the same way
 * `timelineFilters.ts` and `departmentTabs.ts` are, so the logic is testable without rendering.
 */
import type { OrgNode } from '../../services/internalPlaygroundApi';

export interface HiringManagerOption {
  /** The Personio ID / `dim_employee.employee_id` the org tree is keyed on. The proxy resolves it
   * to the Airtable `people_database` record id the link field stores. */
  employeeId: string;
  name: string;
}

/**
 * Every current supervisor in the tree, as manager options.
 *
 * Built from the timeline payload the page has already loaded rather than from a new endpoint —
 * which costs nothing AND makes "the manager must be someone the tree can place this role under"
 * true by construction instead of by a matching rule that could drift. A manager the tree doesn't
 * know would put the proposal in the "Unassigned" bucket, which is precisely the outcome a picker
 * should make impossible.
 *
 * Only NODES are collected — `buildNode` on the proxy already promotes someone to a node the
 * moment they have their own reports or their own open demand, so walking `subManagers` alone is
 * exactly "people who supervise", with no separate flag to keep in step. Individual-contributor
 * leaves (`roleGroups[].entries`) are deliberately not offered: a picker of ~700 names is easier to
 * use when it holds only the supervisors someone would plausibly report a new hire to. The
 * synthetic "Unassigned" bucket is excluded by `isPerson`.
 *
 * Also excludes anyone already terminated as of `now` (`terminationDate` in the past) — the tree
 * itself can carry recent leavers for FTE accounting, but a new proposal should not be filed under
 * someone who is no longer here to manage it. `now` defaults to the real clock and is only ever
 * overridden in tests.
 *
 * Deduplicated by employee id and sorted by name — the list is ~700 people and a picker over it
 * is only usable in a predictable order.
 */
export function hiringManagerOptions(root: OrgNode, now: Date = new Date()): HiringManagerOption[] {
  const byId = new Map<string, HiringManagerOption>();
  const isActive = (terminationDate?: string | null) => !terminationDate || new Date(terminationDate) > now;

  const walk = (node: OrgNode) => {
    if (node.isPerson && isActive(node.terminationDate)) {
      byId.set(node.id, { employeeId: node.id, name: node.personName });
    }
    node.subManagers.forEach(walk);
  };
  walk(root);

  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Unit prefix → the People Team values it plausibly belongs to, for narrowing the Unit dropdown
 * once a Department is picked.
 *
 * Units are free text, `"<short label> - <sub-team>"` (e.g. `"Ops - Fleet Ops"`), and the short
 * label is a related but not identical spelling of the six People Team values — mirrors the
 * read-side `clusterFromUnit` in `proxy-server/lib/rolePortfolio.js` (same `-`/`–`/`—` split, same
 * lowercase-and-compare), kept as its own small map here because that one groups by TA cluster
 * (three buckets), not by the six People Team values this form needs.
 *
 * `Tech` is deliberately absent: a Tech role's Unit follows whichever business unit it serves
 * (`Growth - Tech`, `Fleet - Tech`, `Ops - Tech`, …), not a unit of its own — narrowing its list
 * would hide the normal case rather than a mistake, per `clusterFromPeopleTeam`'s own note that "a
 * Tech role's department follows the business unit it serves".
 */
const DEPARTMENT_UNIT_PREFIXES: Record<string, string[]> = {
  Growth: ['growth'],
  Operations: ['ops', 'operations'],
  Fleet: ['fleet'],
  'Finance & Legal': ['finance'],
  People: ['people'],
};

/**
 * Narrow the Unit choices to the ones that plausibly belong to the selected Department.
 *
 * Falls back to the full list whenever narrowing would not help — no department picked yet, a
 * department (`Tech`) that legitimately spans every unit, or a match set that comes back empty
 * (a naming drift should leave the picker with something pickable, not nothing).
 */
export function unitOptionsForDepartment(units: string[], peopleTeam: string): string[] {
  const prefixes = DEPARTMENT_UNIT_PREFIXES[peopleTeam];
  if (!prefixes) return units;
  const matches = units.filter((unit) => {
    const head = unit.split(/[-–—]/)[0]?.trim().toLowerCase();
    return head ? prefixes.includes(head) : false;
  });
  return matches.length > 0 ? matches : units;
}

/**
 * The most roles one bulk submission may file.
 *
 * Mirrors `MAX_BULK_ROLES` in `proxy-server/lib/internalPlaygroundProposals.js`, which is the one
 * that actually enforces it — this copy exists so the count control can stop at the ceiling
 * rather than letting someone fill in thirty rows and then be refused.
 */
export const MAX_BULK_ROLES = 25;

/** Shared field classes for the propose form. Native `<select>`/`<input>`, not the DS atoms —
 * see the deviation note at the top of `ProposeRoleModal.tsx`. */
export const FIELD_CLASS =
  'w-full rounded border border-zinc-300 bg-white px-2.5 py-1.5 text-sm text-zinc-900 focus:border-trustedBlue focus:outline-none';
export const LABEL_CLASS = 'mb-1 block text-xs font-medium text-zinc-600';

/**
 * Which of the SHARED fields are still empty.
 *
 * A convenience gate in front of the bulk step, not the authority on validity: the proxy
 * re-validates everything and owns the length caps, the date rules and the `CAC` refusal. This
 * only checks emptiness, and only so that nobody fills in twelve rows of dates before being told
 * the title is missing. Keeping it to "is it blank" is deliberate — a second copy of the real
 * rules would be a second thing to keep in step.
 */
export function blankSharedFields(
  shared: Partial<Record<SharedProposalField, string>>
): Partial<Record<SharedProposalField, string>> {
  const missing: Partial<Record<SharedProposalField, string>> = {};
  const require = (key: SharedProposalField, message: string) => {
    if (!(shared[key] ?? '').trim()) missing[key] = message;
  };
  require('title', 'Give the role a title.');
  require('role', 'Pick a role.');
  require('peopleTeam', 'Pick a department (reporting line).');
  require('hiringManagerEmployeeId', 'Pick a hiring manager.');
  require('proposalRational', 'Say why this role is being proposed.');
  return missing;
}

/** The shared fields' keys — everything a bulk submission sends once rather than per role. */
export type SharedProposalField =
  | 'title'
  | 'role'
  | 'peopleTeam'
  | 'hiringManagerEmployeeId'
  | 'proposalRational'
  | 'unit'
  | 'reasonForHiring'
  | 'costCategory'
  | 'priorityRating';

/**
 * Grow or shrink the bulk row list to `count`, keeping what is already filled in.
 *
 * New rows copy the LAST one: the whole premise is several roles that differ only slightly, so
 * "three more like that one" is the common case and re-typing the same date three times is not.
 * Shrinking drops from the end, which is where the untouched rows are.
 */
export function resizeBulkRows<T extends object>(rows: T[], count: number, blank: T): T[] {
  const target = Math.max(1, Math.min(MAX_BULK_ROLES, Math.floor(count) || 1));
  if (target === rows.length) return rows;
  if (target < rows.length) return rows.slice(0, target);
  const seed = rows[rows.length - 1] ?? blank;
  return [...rows, ...Array.from({ length: target - rows.length }, () => ({ ...seed }))];
}
